import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ComposedChart, Bar, ReferenceArea, ReferenceLine
} from 'recharts';
import { calcDOTS, getClosestBodyweight, formatWeight } from '../utils/calculations';

const liftColorMap = { bench: '#4a9eff', squat: '#4aff7f', deadlift: '#ffe04a' };
const bandColors = ['rgba(255,255,255,0.05)', 'rgba(255,255,255,0.12)'];

export default function ProgressTab({ logEntries, bodyweights, prs, settings, progress }) {
  const [showVolume, setShowVolume] = useState(false);
  const [showDots, setShowDots] = useState(true);
  const [liftFilter, setLiftFilter] = useState('all'); // 'all' | 'bench' | 'squat' | 'deadlift'

  // === ALL LIFTS chart data ===
  const allChartData = useMemo(() => {
    const dataByDate = {};
    for (const entry of logEntries) {
      if (!entry.date || !entry.estimatedMax) continue;
      const key = entry.date;
      if (!dataByDate[key]) {
        dataByDate[key] = { date: key, bench: null, squat: null, deadlift: null, benchVol: 0, squatVol: 0, dlVol: 0 };
      }
      const lift = entry.lift;
      if (lift && entry.estimatedMax) {
        if (!dataByDate[key][lift] || entry.estimatedMax > dataByDate[key][lift]) {
          dataByDate[key][lift] = Math.round(entry.estimatedMax * 10) / 10;
        }
      }
      if (lift && entry.tonnage) {
        const volKey = lift === 'deadlift' ? 'dlVol' : `${lift}Vol`;
        dataByDate[key][volKey] += entry.tonnage;
      }
    }

    const sorted = Object.values(dataByDate).sort((a, b) => a.date.localeCompare(b.date));
    let lastBench = null, lastSquat = null, lastDL = null;
    for (const point of sorted) {
      if (point.bench) lastBench = point.bench; else point.bench = lastBench;
      if (point.squat) lastSquat = point.squat; else point.squat = lastSquat;
      if (point.deadlift) lastDL = point.deadlift; else point.deadlift = lastDL;
      const b = point.bench || 0, s = point.squat || 0, d = point.deadlift || 0;
      point.total = b + s + d > 0 ? Math.round((b + s + d) * 10) / 10 : null;
      const bw = getClosestBodyweight(bodyweights, point.date);
      point.dots = bw && point.total ? calcDOTS(point.total, bw.weight) : null;
      const dt = new Date(point.date);
      point.label = `${dt.getDate()}/${dt.getMonth() + 1}`;
    }
    return sorted;
  }, [logEntries, bodyweights]);

  // === PER-LIFT chart data + cycles ===
  const { liftChartData, liftCycles, liftBands, liftSummary } = useMemo(() => {
    if (liftFilter === 'all') return { liftChartData: [], liftCycles: [], liftBands: [], liftSummary: null };

    const lift = liftFilter;
    const liftEntries = logEntries
      .filter(e => e.lift === lift && e.estimatedMax)
      .sort((a, b) => a.date.localeCompare(b.date) || (a.timestamp || 0) - (b.timestamp || 0));

    if (liftEntries.length === 0) return { liftChartData: [], liftCycles: [], liftBands: [], liftSummary: null };

    // Build chart data points
    const data = [];
    const seen = new Set();
    for (const entry of liftEntries) {
      const key = entry.date;
      if (seen.has(key)) {
        // Update existing point if this e1rm is higher
        const existing = data.find(d => d.date === key);
        if (existing && entry.estimatedMax > existing.e1rm) {
          existing.e1rm = Math.round(entry.estimatedMax * 10) / 10;
        }
        continue;
      }
      seen.add(key);
      const dt = new Date(entry.date);
      data.push({
        date: key,
        label: `${dt.getDate()}/${dt.getMonth() + 1}`,
        e1rm: Math.round(entry.estimatedMax * 10) / 10,
        cycle: entry.cycle || 1,
        programId: entry.programId,
        programName: entry.programName,
        tonnage: entry.tonnage || 0,
      });
    }

    // Build cycles from entries
    const cycleMap = {};
    for (const entry of liftEntries) {
      const key = `${entry.programId}_${entry.cycle || 1}`;
      if (!cycleMap[key]) {
        cycleMap[key] = {
          key,
          programId: entry.programId,
          programName: entry.programName || 'Unknown',
          cycle: entry.cycle || 1,
          startDate: entry.date,
          endDate: entry.date,
          startE1rm: entry.estimatedMax,
          endE1rm: entry.estimatedMax,
          sessions: 0,
          startIdx: data.findIndex(d => d.date === entry.date),
        };
      }
      const c = cycleMap[key];
      c.sessions++;
      if (entry.date > c.endDate) {
        c.endDate = entry.date;
        c.endE1rm = entry.estimatedMax;
      }
      if (entry.date < c.startDate) {
        c.startDate = entry.date;
        c.startE1rm = entry.estimatedMax;
      }
      c.endIdx = data.findIndex(d => d.date === entry.date);
      // Track best e1rm
      if (!c.bestE1rm || entry.estimatedMax > c.bestE1rm) {
        c.bestE1rm = entry.estimatedMax;
      }
    }

    const cycles = Object.values(cycleMap).sort((a, b) => a.startDate.localeCompare(b.startDate));

    // Build seamless bands
    const bands = cycles.map((c, i) => {
      const startLabel = data[c.startIdx]?.label;
      const endLabel = i < cycles.length - 1 ? data[cycles[i + 1].startIdx]?.label : data[c.endIdx]?.label;
      return { ...c, startLabel, endLabel, bandColor: bandColors[i % 2] };
    });

    // Summary
    const currentCycleNum = progress[lift]?.cycle || 1;
    const currentProgId = settings[`${lift}Program`];
    const startingE1rm = data.length > 0 ? data[0].e1rm : 0;
    const currentE1rm = data.length > 0 ? data[data.length - 1].e1rm : 0;
    const uniquePrograms = new Set(cycles.map(c => c.programId)).size;

    const summary = {
      startingE1rm,
      currentE1rm,
      totalProgress: currentE1rm - startingE1rm,
      programsRun: uniquePrograms,
      cyclesCompleted: cycles.filter(c => !(c.programId === currentProgId && c.cycle === currentCycleNum)).length,
      inProgress: cycles.some(c => c.programId === currentProgId && c.cycle === currentCycleNum),
    };

    return { liftChartData: data, liftCycles: cycles, liftBands: bands, liftSummary: summary };
  }, [logEntries, liftFilter, progress, settings]);

  // PR history for single lift view
  const liftPRs = useMemo(() => {
    if (liftFilter === 'all') return [];
    return prs.filter(p => p.lift === liftFilter).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [prs, liftFilter]);

  // PR history for all view
  const prsByLift = useMemo(() => {
    const result = { bench: [], squat: [], deadlift: [] };
    for (const pr of prs) {
      if (result[pr.lift]) result[pr.lift].push(pr);
    }
    for (const lift of Object.keys(result)) {
      result[lift].sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    return result;
  }, [prs]);

  const hasData = liftFilter === 'all' ? allChartData.length > 0 : liftChartData.length > 0;

  if (!hasData) {
    return (
      <div>
        <div className="session-header">
          <h1>Progress</h1>
          <p className="sub">Track your strength over time</p>
        </div>
        <LiftFilterBar liftFilter={liftFilter} setLiftFilter={setLiftFilter} />
        <div className="empty-state">
          <h3>No data yet</h3>
          <p>Log sessions with AMAP sets to start tracking estimated 1RMs.</p>
        </div>
      </div>
    );
  }

  // === PER-LIFT VIEW ===
  if (liftFilter !== 'all') {
    const liftColor = liftColorMap[liftFilter];
    const liftName = liftFilter.charAt(0).toUpperCase() + liftFilter.slice(1);
    const currentProg = progress[liftFilter];
    const currentCycleNum = currentProg?.cycle || 1;
    const currentProgId = settings[`${liftFilter}Program`];

    return (
      <div>
        <div className="session-header">
          <h1><span style={{ color: liftColor }}>{liftName}</span> Progress</h1>
          <p className="sub">Estimated 1RM over time · {liftCycles.length} cycle{liftCycles.length !== 1 ? 's' : ''}</p>
        </div>

        <LiftFilterBar liftFilter={liftFilter} setLiftFilter={setLiftFilter} />

        {/* Chart */}
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={liftChartData} margin={{ top: 10, right: showVolume ? 50 : 12, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="label" tick={{ fill: '#666', fontSize: 10 }} interval={Math.max(1, Math.floor(liftChartData.length / 8))} />
              <YAxis yAxisId="left" tick={{ fill: '#666', fontSize: 11 }} domain={['auto', 'auto']} />
              {showVolume && (
                <YAxis yAxisId="vol" orientation="right" tick={{ fill: '#666', fontSize: 10 }} domain={['auto', 'auto']}
                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}t` : v} />
              )}
              <Tooltip
                contentStyle={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#888' }}
                formatter={(value, name) => [name === 'Volume' ? `${(value/1000).toFixed(1)}t` : `${value} kg`, name]}
              />

              {/* Cycle bands */}
              {liftBands.map((band, i) => (
                <ReferenceArea key={i} yAxisId="left" x1={band.startLabel} x2={band.endLabel}
                  fill={band.bandColor} fillOpacity={1} />
              ))}

              {/* Cycle boundary lines */}
              {liftCycles.slice(1).map((c, i) => {
                const label = liftChartData[c.startIdx]?.label;
                return label ? <ReferenceLine key={i} yAxisId="left" x={label} stroke="#444" strokeDasharray="4 4" strokeWidth={1} /> : null;
              })}

              <Line yAxisId="left" type="monotone" dataKey="e1rm" stroke={liftColor} strokeWidth={2.5}
                dot={false} activeDot={{ r: 5, fill: liftColor, stroke: '#0d0d0d', strokeWidth: 2 }}
                name="Est. 1RM" />

              {showVolume && (
                <Bar yAxisId="vol" dataKey="tonnage" fill={`${liftColor}33`} name="Volume" />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          {/* Cycle labels under chart */}
          {liftCycles.length > 1 && (
            <div style={{ display: 'flex', marginTop: 4, overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 4px' }}>
              {liftCycles.map((c, i) => {
                const total = liftChartData.length;
                const startPct = total > 1 ? (c.startIdx / (total - 1)) * 100 : 0;
                const endPct = total > 1 ? (c.endIdx / (total - 1)) * 100 : 100;
                const widthPct = Math.max(endPct - startPct, 10);
                return (
                  <div key={c.key} style={{
                    minWidth: 70, width: `${widthPct}%`, flexShrink: 0,
                    textAlign: 'center', padding: '6px 4px', borderRadius: 4,
                    background: bandColors[i % 2],
                  }}>
                    <div style={{
                      fontFamily: 'Barlow Condensed, sans-serif', fontSize: 11, fontWeight: 600,
                      color: liftColor, letterSpacing: '0.03em',
                    }}>{c.programName}</div>
                    <div style={{
                      fontFamily: 'Barlow Condensed, sans-serif', fontSize: 10, color: 'var(--text-dim)',
                    }}>C{c.cycle} · {formatWeight(Math.round(c.bestE1rm * 10) / 10)}kg</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Toggle */}
        <div className="chart-toggle">
          <button className={showVolume ? 'active' : ''} onClick={() => setShowVolume(!showVolume)}>Volume</button>
        </div>

        {/* Summary */}
        {liftSummary && (
          <div className="card">
            <div className="card-header">{liftName} Summary</div>
            {[
              { label: 'Starting 1RM', value: `${formatWeight(liftSummary.startingE1rm)} kg`, color: 'var(--text-dim)' },
              { label: 'Current 1RM', value: `${formatWeight(liftSummary.currentE1rm)} kg`, color: liftColor },
              { label: 'Total Progress', value: `+${formatWeight(liftSummary.totalProgress)} kg`, color: 'var(--success)' },
              { label: 'Programs Run', value: `${liftSummary.programsRun}`, color: 'var(--text-dim)' },
              { label: 'Cycles Completed', value: `${liftSummary.cyclesCompleted}${liftSummary.inProgress ? ' (1 in progress)' : ''}`, color: 'var(--text-dim)' },
            ].map((stat, i) => (
              <div key={i} className="profile-stat">
                <span className="profile-stat-label">{stat.label}</span>
                <span className="profile-stat-value" style={{ color: stat.color }}>{stat.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Cycle History */}
        <div className="card-header" style={{ marginTop: 8 }}>Cycle History</div>
        {[...liftCycles].reverse().map((c, i) => {
          const isInProgress = c.programId === currentProgId && c.cycle === currentCycleNum;
          const prevCycle = [...liftCycles].reverse()[i + 1];
          const gain = prevCycle ? c.bestE1rm - prevCycle.bestE1rm : 0;
          return (
            <div key={c.key} className="cycle-history-card">
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 15, fontWeight: 600 }}>
                  {c.programName} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Cycle {c.cycle}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {fmtDateShort(c.startDate)} – {fmtDateShort(c.endDate)} · {c.sessions} session{c.sessions !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 20, fontWeight: 700, color: liftColor }}>
                  {formatWeight(Math.round(c.bestE1rm * 10) / 10)} kg
                </div>
                <div style={{
                  fontSize: 11, fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 600,
                  color: isInProgress ? liftColor : 'var(--success)',
                }}>
                  {isInProgress ? 'IN PROGRESS' : gain > 0 ? `+${formatWeight(Math.round(gain * 10) / 10)}kg` : gain < 0 ? `${formatWeight(Math.round(gain * 10) / 10)}kg` : '—'}
                </div>
              </div>
            </div>
          );
        })}

        {/* PRs */}
        {liftPRs.length > 0 && (
          <div className="card" style={{ marginTop: 12 }}>
            <div className="card-header">Personal Records</div>
            {liftPRs.map((pr, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                fontSize: 13, color: 'var(--text-dim)', borderBottom: i < liftPRs.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                <span>{fmtDateShort(pr.date)}</span>
                <span>
                  {pr.achievedWith && pr.achievedWith.loggedWeight > 0 && (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 8 }}>
                      {formatWeight(pr.achievedWith.loggedWeight)}×{pr.achievedWith.loggedReps}
                    </span>
                  )}
                  <span style={{ color: 'var(--pr-color)', fontWeight: 600 }}>{formatWeight(pr.estimatedMax)}kg</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // === ALL LIFTS VIEW ===
  return (
    <div>
      <div className="session-header">
        <h1>Progress</h1>
        <p className="sub">Estimated 1RM over time</p>
      </div>

      <LiftFilterBar liftFilter={liftFilter} setLiftFilter={setLiftFilter} />

      <div className="chart-toggle">
        <button className={showDots ? 'active' : ''} onClick={() => { setShowDots(!showDots); if (!showDots) setShowVolume(false); }}>DOTS</button>
        <button className={showVolume ? 'active' : ''} onClick={() => { setShowVolume(!showVolume); if (!showVolume) setShowDots(false); }}>Volume</button>
      </div>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={allChartData} margin={{ top: 5, right: (showDots || showVolume) ? 50 : 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis dataKey="label" tick={{ fill: '#888', fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fill: '#888', fontSize: 11 }} domain={['auto', 'auto']} />
            {showDots && <YAxis yAxisId="right" orientation="right" tick={{ fill: '#ff8c4a', fontSize: 11 }} domain={['auto', 'auto']} />}
            {showVolume && <YAxis yAxisId="vol" orientation="right" tick={{ fill: '#666', fontSize: 10 }} domain={['auto', 'auto']}
              tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}t` : v} />}
            <Tooltip contentStyle={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} labelStyle={{ color: '#888' }} />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="bench" stroke="#4a9eff" strokeWidth={2} dot={false} name="Bench" connectNulls />
            <Line yAxisId="left" type="monotone" dataKey="squat" stroke="#4aff7f" strokeWidth={2} dot={false} name="Squat" connectNulls />
            <Line yAxisId="left" type="monotone" dataKey="deadlift" stroke="#ffe04a" strokeWidth={2} dot={false} name="Deadlift" connectNulls />
            <Line yAxisId="left" type="monotone" dataKey="total" stroke="#ffffff" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Total" connectNulls />
            {showDots && <Line yAxisId="right" type="monotone" dataKey="dots" stroke="#ff8c4a" strokeWidth={2} dot={false} name="DOTS" connectNulls />}
            {showVolume && (
              <>
                <Bar yAxisId="vol" dataKey="benchVol" fill="#4a9eff33" name="Bench Vol" />
                <Bar yAxisId="vol" dataKey="squatVol" fill="#4aff7f33" name="Squat Vol" />
                <Bar yAxisId="vol" dataKey="dlVol" fill="#ffe04a33" name="DL Vol" />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Current stats */}
      <div className="card">
        <div className="card-header">Estimated 1RM (from performance)</div>
        {['bench', 'squat', 'deadlift'].map(lift => {
          const latest = allChartData.length > 0 ? allChartData[allChartData.length - 1][lift] : null;
          return (
            <div key={lift} className="profile-stat">
              <span className="profile-stat-label" style={{ color: liftColorMap[lift], textTransform: 'capitalize' }}>{lift}</span>
              <span className="profile-stat-value">{latest ? `${formatWeight(latest)}kg` : '—'}</span>
            </div>
          );
        })}
        {allChartData.length > 0 && allChartData[allChartData.length - 1].total && (
          <>
            <div className="profile-stat">
              <span className="profile-stat-label">Total</span>
              <span className="profile-stat-value">{formatWeight(allChartData[allChartData.length - 1].total)}kg</span>
            </div>
            {allChartData[allChartData.length - 1].dots && (
              <div className="profile-stat">
                <span className="profile-stat-label" style={{ color: 'var(--dots-color)' }}>DOTS</span>
                <span className="profile-stat-value" style={{ color: 'var(--dots-color)' }}>
                  {allChartData[allChartData.length - 1].dots.toFixed(1)}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* PR History */}
      <div className="card">
        <div className="card-header">Personal Records</div>
        {['bench', 'squat', 'deadlift'].map(lift => {
          const lps = prsByLift[lift] || [];
          if (lps.length === 0) return (
            <div key={lift} style={{ padding: '8px 0', fontSize: 13, color: 'var(--text-muted)' }}>No {lift} PRs yet</div>
          );
          return (
            <div key={lift} style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 13, fontWeight: 600, color: liftColorMap[lift], textTransform: 'uppercase', marginBottom: 4 }}>
                {lift}
              </div>
              {lps.map((pr, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: 'var(--text-dim)', borderBottom: '1px solid var(--border)' }}>
                  <span>{fmtDateShort(pr.date)}</span>
                  <span style={{ color: 'var(--pr-color)', fontWeight: 600 }}>{formatWeight(pr.estimatedMax)}kg</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LiftFilterBar({ liftFilter, setLiftFilter }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
      {['all', 'bench', 'squat', 'deadlift'].map(f => (
        <button key={f} onClick={() => setLiftFilter(f)} style={{
          flex: 1, padding: '8px 0', borderRadius: 8,
          border: `1px solid ${liftFilter === f ? (f === 'all' ? 'var(--accent)' : liftColorMap[f]) : 'var(--border)'}`,
          background: liftFilter === f ? (f === 'all' ? 'var(--bg-elevated)' : `${liftColorMap[f]}15`) : 'none',
          color: liftFilter === f ? (f === 'all' ? 'var(--accent)' : liftColorMap[f]) : 'var(--text-dim)',
          fontFamily: 'Barlow Condensed, sans-serif', fontSize: 13, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer',
        }}>
          {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
        </button>
      ))}
    </div>
  );
}

function fmtDateShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
